import {
  AnalyticsActionLoginEvent,
  AnalyticsActionLogoutEvent,
  AnalyticsEventParam,
} from '@accedo/one-plugin-sdk';
import { LS } from 'ls-ts';
import { v4 as uuidv4 } from 'uuid';
import { proxy, useSnapshot } from 'valtio';

import {
  AuthenticationType,
  Entitlements,
  Profile,
  ProfileResponse,
  TokenResponse,
  userService,
} from '#/client';
import { CoreAuthFeature } from '#/service/config';
import { StorageKeys } from '#/service/constants';
import { getDeviceInfo } from '#/service/user/userSessionManager';

import { WrappedStore } from '../common';

export interface AuthState {
  /* is standard or TVE login enabled based on core authentication config */
  isAuthEnabled: boolean;
  isInitialised: boolean;
  /* is user logged in */
  isLoggedIn: boolean;
  /* user has subscription or not */
  isEntitled: boolean;
  /* expiration date of subscription */
  expirationDate: string;
  type: AuthenticationType;
  userId?: string;
  sessionId?: string;
  profile: Profile;
  entitlements: Entitlements;
  /* authentication config from core */
  config?: CoreAuthFeature;
  /* is login modal open */
  isLoginShown: boolean;
  purchaseEntitlements?: string[];
  /* is standard login type enabled in config */
  isStandardLogin: boolean;
  /* is standard login related forgot password feature enabled in core authentication config */
  isForgotPasswordEnabled: boolean;
  /* is standard login related registration feature enabled in core authentication config */
  isRegistrationEnabled: boolean;
  /* is second screen login feature enabled in core authentication config */
  isSecondScreen: boolean;
  /* is TVE login feature enabled in core authentication config */
  isTve: boolean;
}

export const defaultAuthState: AuthState = {
  isAuthEnabled: false,
  isInitialised: false,
  isLoggedIn: false,
  isEntitled: false,
  isLoginShown: false,
  expirationDate: '',
  type: AuthenticationType.None,
  entitlements: {
    subscriber: false,
    grantedEntitlementTags: [],
  },
  profile: {
    attributes: {},
    preferences: {},
  },
  isStandardLogin: false,
  isForgotPasswordEnabled: false,
  isRegistrationEnabled: false,
  isSecondScreen: false,
  isTve: false,
};

const _authStore = proxy<WrappedStore<AuthState>>({
  value: defaultAuthState,
});

/**
 * Object representing Auth state.
 * Please DO NOT use it directly in the app's code (tests and other stores are exception here).
 * Instead, you should use:
 * @example const { ... } = useAuthentication();
 **/
export const authStore = _authStore.value;

/**
 * Function setting useful flags from authentication core config to auth store.
 *
 * @config: core authentication config
 **/
const setAuthenticationFeatures = (config: CoreAuthFeature) => {
  authStore.isAuthEnabled =
    !!config?.standard?.enabled || !!config?.tve?.enabled;
  authStore.isStandardLogin = !!config?.standard?.enabled;
  authStore.isForgotPasswordEnabled =
    !!config?.standard?.forgotPassword?.enabled;
  authStore.isRegistrationEnabled = !!config?.standard?.registration?.enabled;
  authStore.isTve = !!config?.tve?.enabled;
  authStore.isSecondScreen = !!config?.secondScreen?.enabled;
};

export const authActions = {
  updateProfile(profile: ProfileResponse) {
    authStore.type = profile.authentication.type;
    authStore.userId = profile.userId;
    authStore.profile = profile.profile;
    authStore.entitlements = profile.entitlements;

    if (!authStore.sessionId) {
      authStore.sessionId = uuidv4();
    }

    authStore.entitlements.grantedEntitlementTags ||= [];

    authStore.isLoggedIn = !!authStore.userId;
    authStore.isEntitled = authStore.entitlements.subscriber || false;
    authStore.expirationDate = profile.authentication.expiration;
  },

  setProfileAttribute(fieldName: string, fieldValue: any) {
    authStore.profile.attributes[fieldName] = fieldValue;
  },

  setProfilePreferences(fieldName: string, fieldValue: any) {
    authStore.profile.preferences[fieldName] = fieldValue;
  },

  showLogin() {
    authStore.isLoginShown = true;
  },

  hideLogin() {
    authStore.isLoginShown = false;
  },

  // TODO#configStore: auth config should be moved to a separate configStore
  setConfig(config: CoreAuthFeature) {
    authStore.config = config;

    setAuthenticationFeatures(config);
  },

  setPurchaseEntitlements(entitlements?: string[]) {
    authStore.purchaseEntitlements = entitlements;
  },

  /** Logs user out by executing all necessary things like:
   * - updating store
   * - firing necessary analytics events
   * - removing saved data from local storage
   */
  async logout() {
    document.dispatchEvent(
      new AnalyticsActionLogoutEvent({
        [AnalyticsEventParam.UserId]: authStore.userId,
        [AnalyticsEventParam.SessionId]: authStore.sessionId ?? '',
      })
    );
    await getAnonymousToken();

    logout();
    LS.remove(StorageKeys.AUTHENTICATION);
  },

  /**
   * When you authenticated user correctly use this method to update
   * store accordingly and kick off necessary analytic events
   **/
  async login(tokenResponse: TokenResponse) {
    const profile = await authActions.refreshProfile();
    saveAuthToken(tokenResponse);

    document.dispatchEvent(
      new AnalyticsActionLoginEvent({
        [AnalyticsEventParam.UserId]: authStore.userId,
        [AnalyticsEventParam.SessionId]: authStore.sessionId,
      })
    );
    authActions.hideLogin();

    return profile;
  },

  async refreshProfile() {
    try {
      const profileRes = await userService.getProfile();
      const profile = profileRes.data;

      authActions.updateProfile(profile);

      return {
        userId: authStore.userId,
        sessionId: authStore.sessionId,
        errors: profile._meta.errors,
        subscribed: profile.subscriber ?? false,
      };
    } catch (err) {
      return {
        userId: authStore.userId,
        sessionId: authStore.sessionId,
        subscribed: false,
      };
    }
  },
};

export const useAuthentication = () => useSnapshot(authStore) as AuthState;

export async function initAuth() {
  // The authentication state is maintained via cookies:
  // to read it, send a request to get the user profile and determine if any was provided
  try {
    await authActions.refreshProfile();
  } catch (err) {
    // if the profile request fails, we will get an anonymous token instead.
  }

  if (authStore.type === AuthenticationType.None) {
    await getAnonymousToken();
  }

  // now we have a token, either authenticated or anonymous.
  authStore.isInitialised = true;
}

/** A way to reset auth store to the initial state. It is only meant to be used in tests! */
export function resetAuth() {
  _authStore.value = defaultAuthState;
}

async function getAnonymousToken() {
  const reqBody = {
    deviceInfo: getDeviceInfo(),
  };

  await userService.getAnonymousToken(reqBody);
}

interface AuthTokenLs extends Omit<TokenResponse, 'token' | 'refreshToken'> {
  isLoggedIn: boolean;
}

function saveAuthToken(tokenResponse: TokenResponse) {
  const { token, refreshToken, ...rest } = tokenResponse;

  const lsToken: AuthTokenLs = {
    isLoggedIn: Boolean(token && refreshToken),
    ...rest,
  };

  LS.write(StorageKeys.AUTHENTICATION, lsToken);
}

function logout() {
  authStore.isLoggedIn = false;
  authStore.isEntitled = false;
  authStore.expirationDate = '';

  authStore.type = AuthenticationType.None;
  authStore.userId = undefined;
  authStore.sessionId = undefined;
  authStore.profile = {
    attributes: {},
    preferences: {},
  };
  authStore.entitlements = {
    grantedEntitlementTags: [],
    subscriber: false,
  };
}
